import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { CosmosCrest } from '@/components/brand/CosmosCrest'
import { Button } from '@/components/ui/button'
import { ParticleBalls } from '@/components/motion/ParticleBalls'
import { useHeroSlides } from '@/lib/hooks'
import type { HeroSlide } from '@/types/api'
import { cn } from '@/lib/utils'

const AUTO_ADVANCE_MS = 6500

export function HeroSlider() {
  const { data: slides = [] } = useHeroSlides()
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const timerRef = useRef<number | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const advance = useCallback(
    (dir: 1 | -1 = 1) => {
      setIndex((i) => (slides.length ? (i + dir + slides.length) % slides.length : 0))
    },
    [slides.length]
  )

  useEffect(() => {
    if (paused || !slides.length) return
    timerRef.current = window.setTimeout(() => advance(1), AUTO_ADVANCE_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [index, paused, advance, slides.length])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') advance(1)
      if (e.key === 'ArrowLeft') advance(-1)
    }
    function handleVis() {
      const v = videoRef.current
      if (!v) return
      if (document.hidden) v.pause()
      else v.play().catch(() => {})
    }
    window.addEventListener('keydown', handleKey)
    document.addEventListener('visibilitychange', handleVis)
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.removeEventListener('visibilitychange', handleVis)
    }
  }, [advance])

  if (!slides.length) {
    return <section className="min-h-[600px] pitch-bg" aria-hidden />
  }

  const slide = slides[index]

  return (
    <section
      className="relative overflow-hidden text-white bg-pitch-950 min-h-[640px] md:min-h-[720px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Cosmos F.C. featured highlights"
    >
      {/* ============ MEDIA LAYER ============ */}
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          {slide.kind === 'video' && slide.video ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover motion-reduce:hidden"
              src={slide.video}
              poster={slide.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ filter: 'blur(2px) brightness(0.72) saturate(1.15)', transform: 'scale(1.06)' }}
            />
          ) : (
            <img
              src={slide.image ?? slide.poster}
              alt=""
              className={cn(
                'absolute inset-0 w-full h-full object-cover origin-center',
                index % 2 === 0 ? 'motion-safe:animate-ken-burns' : 'motion-safe:animate-ken-burns-reverse'
              )}
              style={{ filter: 'brightness(0.72) saturate(1.15)' }}
              loading="eager"
              decoding="async"
            />
          )}
          {slide.kind === 'video' && slide.poster && (
            <img
              src={slide.poster}
              alt=""
              aria-hidden
              className="motion-safe:hidden absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.7)' }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Gradient overlays for legibility (darker left, softer right) */}
      <div className="absolute inset-0 bg-gradient-to-r from-pitch-950/90 via-pitch-950/65 to-pitch-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-pitch-950/55 via-transparent to-pitch-950/20" />
      <div className="absolute inset-0 [background:radial-gradient(circle_at_82%_18%,rgba(245,197,24,0.16),transparent_50%),radial-gradient(circle_at_12%_88%,rgba(28,168,74,0.14),transparent_45%)]" />

      {/* Ambient particle balls */}
      <ParticleBalls count={7} />

      {/* ============ CONTENT LAYER ============ */}
      <div className="container-narrow relative z-10 flex flex-col justify-center min-h-[640px] md:min-h-[720px] py-24 md:py-28">
        <div className="grid gap-14 md:grid-cols-[1.15fr_0.85fr] items-center">
          <div className="min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={slide.id + '-content'} className="flex flex-col">
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5 }}
                  className="eyebrow eyebrow-gold [text-shadow:_0_2px_8px_rgba(0,0,0,0.7)]"
                >
                  {slide.eyebrow}
                </motion.span>

                <SplitHeadline text={slide.headline} highlight={slide.highlight} />

                <motion.p
                  key={slide.id + '-sub'}
                  initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)' }}
                  animate={{ opacity: 1, clipPath: 'inset(0 0% 0 0)' }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: 0.55, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-6 text-base md:text-lg text-white max-w-xl leading-relaxed [text-shadow:_0_2px_10px_rgba(0,0,0,0.65)]"
                >
                  {slide.subhead}
                </motion.p>

                <motion.div
                  key={slide.id + '-cta'}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: 0.75, duration: 0.5, type: 'spring', damping: 18 }}
                  className="mt-8 flex flex-wrap gap-3"
                >
                  <Button asChild size="lg">
                    <Link to={slide.primaryCta.to}>
                      {slide.primaryCta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    <Link to={slide.secondaryCta.to}>
                      <PlayCircle className="h-4 w-4" /> {slide.secondaryCta.label}
                    </Link>
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.5 }}
              className="mt-14 grid grid-cols-3 gap-6 max-w-md"
            >
              {[
                { n: '15+', l: 'Squads' },
                { n: '10', l: 'Programs' },
                { n: '2', l: 'Countries' },
              ].map((s) => (
                <div key={s.l}>
                  <div className="text-3xl md:text-4xl font-display num text-cosmos-gold">{s.n}</div>
                  <div className="text-[10px] tracking-widest uppercase text-white/60 mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="hidden md:flex justify-center relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: -15 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative motion-safe:animate-float-slow"
            >
              <div className="absolute inset-0 rounded-full bg-cosmos-gold/25 blur-3xl scale-110 motion-safe:animate-pulse-glow" aria-hidden />
              <div
                className="absolute inset-0 rounded-full bg-cosmos-green/20 blur-3xl scale-125 motion-safe:animate-pulse-glow"
                aria-hidden
                style={{ animationDelay: '1.5s' }}
              />
              <CosmosCrest size={260} animated />
            </motion.div>
          </div>
        </div>
      </div>

      {/* ============ CONTROLS ============ */}
      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-4 px-6">
        <button
          onClick={() => advance(-1)}
          aria-label="Previous slide"
          className="grid h-9 w-9 place-items-center rounded-full border border-white/25 bg-black/30 text-white backdrop-blur hover:bg-cosmos-gold hover:border-cosmos-gold hover:text-pitch-950 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={cn(
                'h-2 rounded-full transition-all overflow-hidden',
                i === index ? 'w-12 bg-white/30' : 'w-2 bg-white/40 hover:bg-white/70'
              )}
            >
              {i === index && (
                <motion.div
                  key={`bar-${index}`}
                  className="h-full rounded-full bg-cosmos-gold"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
                />
              )}
            </button>
          ))}
        </div>

        <button
          onClick={() => advance(1)}
          aria-label="Next slide"
          className="grid h-9 w-9 place-items-center rounded-full border border-white/25 bg-black/30 text-white backdrop-blur hover:bg-cosmos-gold hover:border-cosmos-gold hover:text-pitch-950 transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  )
}

/**
 * Letter-by-letter split reveal for the headline, with a gradient underline sweep
 * that fires under the `highlight` phrase. Words are wrapped so letters within
 * a word never wrap across lines (fixes "COSMOS F.C." mid-word break).
 */
function SplitHeadline({ text, highlight }: { text: string; highlight: string }) {
  const parts = splitAroundHighlight(text, highlight)
  return (
    <motion.h1
      className="mt-6 font-display uppercase leading-[0.9] text-5xl md:text-7xl xl:text-8xl [text-shadow:_0_4px_24px_rgba(0,0,0,0.55),_0_1px_2px_rgba(0,0,0,0.7)]"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.025, delayChildren: 0.12 } },
      }}
    >
      {parts.map((part, pi) => {
        const tokens = part.text.split(/(\s+)/)
        return (
          <span key={pi} className={cn('inline', part.isHighlight && 'relative text-cosmos-gold')}>
            {tokens.map((tok, ti) => {
              if (tok.length === 0) return null
              if (/^\s+$/.test(tok)) {
                return (
                  <span key={`${pi}-t${ti}`}>{' '}</span>
                )
              }
              return (
                <span key={`${pi}-t${ti}`} className="inline-block whitespace-nowrap">
                  {Array.from(tok).map((ch, ci) => (
                    <motion.span
                      key={`${pi}-${ti}-${ci}`}
                      className="inline-block"
                      variants={{
                        hidden: { y: 40, opacity: 0 },
                        visible: { y: 0, opacity: 1 },
                      }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {ch}
                    </motion.span>
                  ))}
                </span>
              )
            })}
            {part.isHighlight && (
              <motion.span
                aria-hidden
                className="absolute left-0 right-0 -bottom-2 h-1.5 rounded-full origin-left bg-gradient-to-r from-cosmos-green via-cosmos-gold to-cosmos-green shadow-[0_0_20px_rgba(245,197,24,0.7)]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.9 + part.text.length * 0.02, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              />
            )}
          </span>
        )
      })}
    </motion.h1>
  )
}

function splitAroundHighlight(text: string, highlight: string) {
  if (!highlight || !text.toLowerCase().includes(highlight.toLowerCase())) {
    return [{ text, isHighlight: false }]
  }
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase())
  const before = text.slice(0, idx)
  const match = text.slice(idx, idx + highlight.length)
  const after = text.slice(idx + highlight.length)
  const parts: { text: string; isHighlight: boolean }[] = []
  if (before) parts.push({ text: before, isHighlight: false })
  parts.push({ text: match, isHighlight: true })
  if (after) parts.push({ text: after, isHighlight: false })
  return parts
}
// Suppress unused-type warning
export type _HeroSlide = HeroSlide
